import React, { memo } from "react";
import { ChevronRight, CircleAlert } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselScrim,
} from "../ui";
import { StudentCardSkeleton } from "./StudentCardSkeleton";
import { StudentCard } from "./StudentCard";
import { EmptyStateComponent } from "../EmptyStateComponent";
import { useTranslation } from "react-i18next";
import { Student } from "@/types/Student";
import { cn } from "@/lib/utils";
import { ApolloError } from "@apollo/client";
import { ProtectedNavLink } from "../ProtectedNavigationLink";
import { RouteId } from "@/routes/getRoute";
import { UrlQueryObject } from "@/lib/UrlSerializers";
import { AnalyticsEvent } from "@/hooks/useAnalytics";

interface StudentCarouselProps {
  students?: Partial<Student>[];
  title: string;
  isLoading?: boolean;
  showLocation?: boolean;
  className?: {
    root?: string;
    title?: string;
    carousel?: RouteId;
  };
  viewAllLink?: RouteId;
  viewAllParams?: Record<string, string>;
  viewAllQuery?: UrlQueryObject;
  viewAllSource?: string;
  profileSource?: string;
  error?: ApolloError | string | undefined;
}

const SKELETON_CARD_COUNT = 12;

export const StudentCarousel = memo(function StudentCarousel({
  title,
  students = [],
  isLoading = false,
  showLocation = false,
  className,
  viewAllLink,
  viewAllParams,
  viewAllQuery,
  viewAllSource,
  profileSource,
  error = undefined,
}: StudentCarouselProps) {
  return (
    <Card>
      <CardContent
        className={cn(
          "relative flex h-full w-full flex-col gap-2 p-6",
          className?.root,
        )}
      >
        <h2 className={cn("text-[16px] font-bold", className?.title)}>
          {title}
        </h2>

        {(!students || students.length === 0) && !isLoading ? (
          <EmptyStateComponent />
        ) : (
          <Carousel
            opts={{ align: "start" }}
            className={cn("relative w-full", className?.carousel)}
            data-testid="student-carousel"
            aria-label={title}
          >
            <CarouselScrim />
            {error ? (
              <div className="flex h-[180px] flex-col items-center justify-center">
                <CircleAlert className="h-12 w-12 text-muted-foreground" />
                <div className="text-center text-sm text-muted-foreground">
                  {typeof error === "string" ? error : error?.message}
                </div>
              </div>
            ) : (
              <CarouselContent className="-ml-32px">
                {isLoading ? (
                  Array.from({ length: SKELETON_CARD_COUNT }).map(
                    (_, index) => <StudentCardSkeleton key={index} />,
                  )
                ) : (
                  <>
                    {students?.map((student, idx) => (
                      <StudentCard
                        key={student.id}
                        student={student}
                        idx={idx}
                        showLocation={showLocation}
                        profileSource={profileSource}
                      />
                    ))}
                    {viewAllLink && (
                      <AllStudentsCard
                        link={viewAllLink}
                        params={viewAllParams}
                        query={viewAllQuery}
                        source={viewAllSource}
                      />
                    )}
                  </>
                )}
              </CarouselContent>
            )}

            <CarouselPrevious
              data-testid="carousel-previous"
              className="absolute top-[40%] -left-2 transform rounded-full border p-2.5 shadow-md transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0"
            />
            <CarouselNext
              data-testid="carousel-next"
              className="absolute top-[40%] -right-2 transform rounded-full border p-2.5 shadow-md transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0"
            />
          </Carousel>
        )}
      </CardContent>
    </Card>
  );
});

const AllStudentsCard = ({
  link,
  params,
  query,
  source,
}: {
  link: RouteId;
  params?: Record<string, string>;
  query?: UrlQueryObject;
  source?: string;
}) => {
  const { t } = useTranslation();

  return (
    <CarouselItem
      className="basis-auto px-32px"
      data-testid="all-students-card"
    >
      <div className="relative flex flex-col">
        <div className="flex size-[160px] flex-col items-center justify-center gap-16px rounded-md bg-muted">
          <p className="text-xs text-foreground antialiased">
            {t("studentCarousel.seeMore")}
          </p>
          <Button
            variant="outline"
            asChild
            className="flex gap-8px rounded-sm border px-3"
          >
            <ProtectedNavLink
              route={link}
              params={params}
              query={query}
              analyticsEvent={AnalyticsEvent.OpenedStudentList}
              analyticsProperties={{ Source: source }}
            >
              {t("studentCarousel.viewAll")}
              <ChevronRight className="h-8px w-4px" />
            </ProtectedNavLink>
          </Button>
        </div>
      </div>
    </CarouselItem>
  );
};
